import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

/**
 * Etiqueta «BETA» de ERIC (PAM-651).
 *
 * Estilos inline a propósito, no una clase CSS: este badge se usa en el buscador
 * del header y en el nav lateral, que se renderizan desde Layout.js en TODA la
 * app, mientras que el CSS de ERIC vive en un <style> dentro de EricAnalyst y
 * solo está montado en la ruta /eric. Una clase dejaría al badge sin estilo en
 * Dashboards, Tickets y el resto de las pantallas.
 *
 * Props:
 *   variante {'compacto'|'destacado'|'discreto'} - 'compacto' (default) en el
 *      buscador del header; 'destacado' en la pantalla de bienvenida, donde hay
 *      más espacio; 'discreto' en el nav lateral, donde la etiqueta no debe
 *      competir con el resto de los módulos del menú.
 *   style {object} - ajustes de POSICIONAMIENTO del sitio que lo usa, no de
 *      apariencia. El header y la bienvenida separan con `gap` en su contenedor
 *      flex; el nav de la plantilla no tiene gap y necesita margen explícito.
 *      El espaciado depende de dónde se inserta el badge, no del badge.
 *
 * Las dos diferencias entre variantes (el punto y el tamaño) van siempre juntas,
 * por eso una prop con valores nombrados en vez de dos booleanos sueltos: así la
 * combinación inválida «con punto pero chico» no se puede expresar.
 */

var BASE = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '3px',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  lineHeight: 1,
  borderRadius: '10px',
  // El input del buscador es flex:1 y comprimiría el badge sin esto.
  flexShrink: 0,
  whiteSpace: 'nowrap',
  // Alineación óptica cuando el badge va inline junto a texto (el caso del nav).
  // Por defecto la baseline del badge se alinea con la del texto, y su padding
  // inferior lo deja ~2.7px por debajo del centro de las mayúsculas. Medido con
  // el CSS real de la plantilla: 0.3em deja el desfase en 0.
  // En los contenedores flex (header y bienvenida) `vertical-align` se ignora,
  // así que este valor es inerte ahí — verificado, no asumido.
  verticalAlign: '0.3em',
};

var VARIANTES = {
  // Turquesa de marca: el badge se lee como una marca sobre fondo claro.
  compacto: {
    fontSize: '9px', padding: '2px 7px',
    background: '#99f6e4', color: '#0f766e',
  },
  destacado: {
    fontSize: '10px', padding: '3px 9px',
    background: '#99f6e4', color: '#0f766e',
  },
  // Nav lateral: hereda el color del texto del ítem con `currentColor`, sin
  // fondo. En una lista densa de módulos una píldora turquesa se lleva el ojo
  // y compite con el resto del menú; acá la etiqueta tiene que acompañar al
  // texto, no destacarse sobre él.
  //
  // `currentColor` en lugar de un hex fijo porque el nav cambia de color según
  // el estado del ítem (normal, hover, activo): así el badge sigue al texto en
  // los tres sin necesitar una regla por estado.
  discreto: {
    fontSize: '9px', padding: '1px 6px',
    background: 'transparent',
    color: 'inherit',
    border: '1px solid currentColor',
    opacity: 0.75,
  },
};

function EricBetaBadge(props) {
  var intl = useIntl();
  var variante = VARIANTES[props.variante] ? props.variante : 'compacto';
  var estilo = Object.assign({}, BASE, VARIANTES[variante], props.style || {});

  return (
    <span
      style={estilo}
      title={intl.formatMessage({ id: 'eric.beta.titulo', defaultMessage: 'Función experimental' })}
    >
      {variante === 'destacado' ? <span aria-hidden="true">●</span> : null}
      <FormattedMessage id="eric.beta.badge" defaultMessage="BETA" />
    </span>
  );
}

export default EricBetaBadge;
